/**
 * Preview Book interior grid: pad to the RunPod batch size and detect completion.
 */

import {
  hasImagesSentToRunPending,
  isLineartJobActiveForBook,
  resolveExpectedInteriorPageCount,
} from './pendingImageStorage';

/** Interior batch-list rows with finished line art (excludes cover). */
export const countFinishedInteriorFromBatchRows = (batchRows) =>
  (batchRows || []).filter(
    (row) =>
      !row?.isCover &&
      !row?.deletedAt &&
      !!row?.AIUrl &&
      String(row.AIUrl).trim(),
  ).length;

/** lineartProgress.currentStage while /run hasn't answered yet (set by Choose Photos). */
export const LINEART_STAGE_STARTING = 'starting';

/**
 * Drawing progress from the server's rows: how many interior pages have their
 * drawing, out of how many, and whether the server is drawing any right now.
 * Page photos and Preview both show this, so they always agree.
 * `starting`: this tab asked for a run and /run hasn't answered yet, so rows
 * can still hold the previous drawings; never call that finished.
 */
export const lineartStatusFromRows = (
  rows,
  bookId,
  { activeBook, lineartProgress } = {},
) => {
  const interiors = (rows || []).filter((row) => !row?.isCover && !row?.deletedAt);
  const done = countFinishedInteriorFromBatchRows(interiors);
  const expected = resolveExpectedInteriorPageCount(bookId, { activeBook, lineartProgress });
  const total = expected > 0 ? expected : interiors.length;
  const drawing = interiors.some(
    (row) => row.isGenerating && !(row.AIUrl && String(row.AIUrl).trim()),
  );
  const starting = lineartProgress?.currentStage === LINEART_STAGE_STARTING;
  return {
    done,
    total,
    drawing,
    starting,
    finished: !starting && !drawing && total > 0 && done >= total,
  };
};

/** True when finished illustration count meets the expected interior page count. */
export const isLineartJobCompleteByCounts = (finishedCount, expectedCount) => {
  const expected = Math.max(0, Number(expectedCount) || 0);
  const finished = Math.max(0, Number(finishedCount) || 0);
  return expected > 0 && finished >= expected;
};

export const isLineartJobCompleteForBatch = (
  batchRows,
  bookId,
  { activeBook, lineartProgress } = {},
) => {
  const expectedCount = resolveExpectedInteriorPageCount(bookId, {
    activeBook,
    lineartProgress,
  });
  return isLineartJobCompleteByCounts(
    countFinishedInteriorFromBatchRows(batchRows),
    expectedCount,
  );
};

/**
 * Cloudinary: do not clear expected page count on per-image events — only when
 * the worker finishes and nothing remains in the sentToRun queue.
 */
export const shouldClearLineartStorageOnCloudinaryComplete = (bookId) =>
  !hasImagesSentToRunPending(bookId);

/**
 * Staging: clear when finished illustration count meets the /run batch size.
 * Cloudinary: use {@link shouldClearLineartStorageOnCloudinaryComplete} on job complete only.
 */
export const shouldClearLineartStorageAfterJob = (
  bookId,
  batchRows,
  { activeBook, lineartProgress, useStagingCountCheck } = {},
) => {
  if (useStagingCountCheck) {
    return isLineartJobCompleteForBatch(batchRows, bookId, {
      activeBook,
      lineartProgress,
    });
  }
  return shouldClearLineartStorageOnCloudinaryComplete(bookId);
};

/** Keep padding while the /run batch still has pages waiting for line art. */
export const shouldPadInteriorGridForLineart = (
  pageItems,
  expectedCount,
  bookId,
  {
    activeBook,
    lineartProgress,
    showLineartProgress = false,
  } = {},
) => {
  const expected = Math.max(0, Number(expectedCount) || 0);
  if (expected === 0) return false;

  if (countFinishedInteriorPages(pageItems) < expected) return true;
  if (hasImagesSentToRunPending(bookId)) return true;
  if (isLineartJobActiveForBook(bookId, { activeBook, lineartProgress })) {
    return true;
  }
  if (lineartProgress?.isProcessing) return true;
  if (showLineartProgress) return true;

  const dbLineartProcessing =
    activeBook?.isProcessing === true &&
    activeBook?.processingWorkflowType === 'lineart' &&
    (activeBook?.processingPercentage ?? 0) < 100;
  return dbLineartProcessing;
};

/** Interior pages with finished line art (excludes cover). */
export const countFinishedInteriorPages = (pageItems) =>
  (pageItems || []).filter(
    (img) =>
      !img?.isCover &&
      !!img.sketchUrl &&
      !img.isLoading &&
      !img.regenerationProcessing,
  ).length;

/**
 * True when every expected interior page has finished line art.
 * Falls back to “all visible pages ready” when expectedCount is unknown.
 */
export const isInteriorGenerationComplete = (
  pageItems,
  expectedCount,
  { jobStillActive = false } = {},
) => {
  const pages = (pageItems || []).filter((img) => !img?.isCover);
  const expected = Math.max(0, Number(expectedCount) || 0);

  if (expected > 0) {
    return countFinishedInteriorPages(pages) >= expected;
  }

  // Do not treat a partial on-screen grid as complete while a job is in flight.
  if (jobStillActive) return false;

  if (pages.length === 0) return false;

  return pages.every(
    (img) =>
      !!img.sketchUrl && !img.isLoading && !img.regenerationProcessing,
  );
};

const firstEmptySlot = (slots) => slots.findIndex((s) => s === null);

/**
 * Ensure the preview grid shows exactly `expectedCount` interior slots while
 * line art is in flight (loading placeholders for pages not in DB yet).
 */
export const padInteriorPagesToExpectedCount = (
  pageItems,
  expectedCount,
  pendingImages = [],
) => {
  const expected = Math.max(0, Number(expectedCount) || 0);
  if (expected === 0) return pageItems || [];

  const slots = new Array(expected).fill(null);
  const overflow = [];

  for (const item of pageItems || []) {
    const rawOrder = item.ordering ?? item.index;
    let slot =
      typeof rawOrder === 'number' &&
      rawOrder >= 0 &&
      rawOrder < expected &&
      slots[rawOrder] === null
        ? rawOrder
        : firstEmptySlot(slots);

    if (slot >= 0) {
      slots[slot] = {
        ...item,
        ordering: item.ordering ?? slot,
        index: item.index ?? slot,
      };
    } else {
      overflow.push(item);
    }
  }

  for (let i = 0; i < expected; i += 1) {
    if (slots[i]) continue;

    const pending = (pendingImages || []).find((p) => p.gridOrdering === i);
    slots[i] = {
      id: pending?.id || `preview-expected-slot-${i}`,
      originalUrl: pending?.url || null,
      sketchUrl: null,
      isCover: false,
      isLoading: true,
      publicId: pending?.publicId || null,
      ordering: i,
      index: i,
      fromDB: false,
      isExpectedSlot: !pending,
    };
  }

  return [...slots, ...overflow];
};
